import AnalyticsService from './analytics/analyticsService';
import { EventName } from './analytics/providers/analyticsProvider';

const SUPPORT_URL = 'https://trimbox.io/support';
const ZENDESK_WAIT_TIMEOUT_MS = 30_000;
const ZENDESK_POLL_INTERVAL_MS = 50;
const LAUNCHER_STYLE_ATTR = 'data-trimbox-zendesk-launcher';

type ZendeskFn = (namespace: string, command: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskFn;
  }
}

let initPromise: Promise<void> | null = null;
let isInitialized = false;
let launcherStyleInjected = false;
/** Once the user opens support, keep the launcher visible (do not re-hide on warm). */
let supportOpened = false;

function getWidgetKey(): string | undefined {
  return process.env.REACT_APP_ZENDESK_WIDGET_KEY;
}

function getEmailFieldId(): string | undefined {
  return process.env.REACT_APP_ZENDESK_EMAIL_FIELD_ID;
}

function isZendeskEnabled(): boolean {
  return Boolean(getWidgetKey());
}

function getZendesk(): ZendeskFn {
  if (!window.zE) {
    throw new Error('Zendesk widget is not loaded');
  }
  return window.zE;
}

function waitForZendesk(): Promise<void> {
  if (window.zE) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const poll = () => {
      if (window.zE) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= ZENDESK_WAIT_TIMEOUT_MS) {
        reject(new Error('Timed out waiting for Zendesk widget to load'));
        return;
      }

      window.setTimeout(poll, ZENDESK_POLL_INTERVAL_MS);
    };

    poll();
  });
}

function runZendeskCommand(
  namespace: string,
  command: string,
  ...args: unknown[]
): void {
  getZendesk()(namespace, command, ...args);
}

/**
 * Keep the default floating launcher invisible. Prefer CSS over messenger hide/show
 * so the widget stays loaded and we never accidentally open the panel on page load.
 * Removed when openSupport runs so the user gets the launcher (close/reopen control).
 */
function ensureLauncherHidden(): void {
  if (launcherStyleInjected || typeof document === 'undefined') {
    return;
  }

  const style = document.createElement('style');
  style.setAttribute(LAUNCHER_STYLE_ATTR, 'true');
  // Prefer stable selectors over localized title attributes (e.g. English-only titles).
  style.textContent = `
    iframe#launcher,
    iframe[data-product="web_widget"],
    div[data-product="web_widget"] iframe {
      display: none !important;
      pointer-events: none !important;
      width: 0 !important;
      height: 0 !important;
      opacity: 0 !important;
    }
  `;
  document.head.appendChild(style);
  launcherStyleInjected = true;
}

/** Reveal the default launcher so users can close/reopen the messenger after Get Support. */
function showLauncher(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll(`style[${LAUNCHER_STYLE_ATTR}]`).forEach((node) => node.remove());
  launcherStyleInjected = false;
}

async function initialize(): Promise<void> {
  if (!isZendeskEnabled()) {
    return;
  }

  if (isInitialized) {
    return;
  }

  if (!initPromise) {
    initPromise = waitForZendesk()
      .then(() => {
        // Do not call messenger hide/show here — hide requires show later, and show
        // was opening the chat panel on page load for this widget config.
        ensureLauncherHidden();
        // Custom-launcher pattern: Zendesk may restore an open session; close once on
        // first init so the panel never appears until the user requests support.
        // Only runs on first initialize (not on later warm() calls).
        runZendeskCommand('messenger', 'close');
        isInitialized = true;
      })
      .catch((error: unknown) => {
        initPromise = null;
        AnalyticsService.trackError(EventName.UNKNOWN_ERROR, error, {
          source: 'zendesk.initialize',
        });
        throw error;
      });
  }

  return initPromise;
}

function setConversationTags(): void {
  runZendeskCommand('messenger:set', 'conversationTags', ['trimbox-web', 'web']);
}

function setEmailContext(email: string): void {
  const fieldId = getEmailFieldId();

  // Fields/metadata attach when the conversation starts; do not block on callbacks.
  if (fieldId) {
    runZendeskCommand('messenger:set', 'conversationFields', [
      { id: fieldId, value: email },
    ]);
    return;
  }

  runZendeskCommand('messenger:set', 'conversationMetadata', { email });
}

function setUserContext(email?: string): void {
  if (!isZendeskEnabled() || !window.zE) {
    return;
  }

  setConversationTags();

  if (email) {
    setEmailContext(email);
  }
}

/**
 * Ensure the snippet is ready and launcher is CSS-hidden. Does not open or show the panel.
 */
async function warm(email?: string): Promise<void> {
  if (!isZendeskEnabled()) {
    return;
  }

  await initialize();
  // After Get Support, leave the launcher visible so the user can close/reopen chat.
  if (!supportOpened) {
    ensureLauncherHidden();
  }
  setUserContext(email);
}

function openMessenger(email?: string): void {
  // Show the floating launcher with the chat panel so the user can close it.
  supportOpened = true;
  showLauncher();
  setUserContext(email);
  // Widget loads visible by default; we only hide the launcher via CSS until open.
  runZendeskCommand('messenger', 'open');
}

async function openSupport(email?: string): Promise<void> {
  if (!isZendeskEnabled()) {
    window.open(SUPPORT_URL);
    return;
  }

  // Hot path: already initialized — open synchronously.
  if (isInitialized && window.zE) {
    openMessenger(email);
    return;
  }

  await initialize();
  openMessenger(email);
}

function resetWidget(): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      runZendeskCommand('messenger', 'resetWidget', () => {
        resolve();
      });
    } catch (error) {
      reject(error);
    }
  });
}

async function logout(): Promise<void> {
  if (!isInitialized) {
    return;
  }

  try {
    await resetWidget();
    isInitialized = false;
    initPromise = null;
    supportOpened = false;
    ensureLauncherHidden();
  } catch (error: unknown) {
    AnalyticsService.trackError(EventName.UNKNOWN_ERROR, error, {
      source: 'zendesk.logout',
    });
  }
}

function setCookies(range: 'all' | 'functional' | 'none'): void {
  if (!window.zE) {
    return;
  }

  runZendeskCommand('messenger:set', 'cookies', range);
}

function hide(): void {
  if (!window.zE) {
    return;
  }

  // Prefer CSS for the launcher. Only use hide when we must fully suppress the widget
  // (e.g. consent none). Callers that hide must show before open will work again.
  runZendeskCommand('messenger', 'hide');
}

const ZendeskService = {
  initialize,
  warm,
  openSupport,
  setUserContext,
  logout,
  setCookies,
  hide,
  isZendeskEnabled,
} as const;

export function __resetZendeskServiceForTests(): void {
  initPromise = null;
  isInitialized = false;
  launcherStyleInjected = false;
  supportOpened = false;
  if (typeof document !== 'undefined') {
    document.querySelectorAll(`style[${LAUNCHER_STYLE_ATTR}]`).forEach((node) => node.remove());
  }
}

export default ZendeskService;
